import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Wrench, ArrowRight, Trash2, Play, AlertTriangle, CheckCircle2, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin "Data Tools" tab — surgical bulk-edit utilities for support cases
 * (e.g. a user accidentally entered all data one day late, needs the date
 * field on a range of daily_entries renamed by ±N days).
 *
 * Always operates via a Preview → Confirm → Execute flow. The Preview call
 * is purely informational (no DB mutation) and lists conflicts up-front so
 * no silent overwrites can happen.
 */
export const DataToolsTab = () => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userId, setUserId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [shiftDays, setShiftDays] = useState(-1);
  const [clearDatesText, setClearDatesText] = useState("");
  const [previewResult, setPreviewResult] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executeResult, setExecuteResult] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      const arr = Array.isArray(res.data) ? res.data : (res.data?.users || []);
      setUsers(arr);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  }, []);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const body = () => ({
    user_id: userId,
    source_start: start,
    source_end: end,
    shift_days: Number(shiftDays),
    clear_dates: clearDatesText
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean),
  });

  const doPreview = async () => {
    setExecuteResult(null);
    setPreviewing(true);
    setPreviewResult(null);
    try {
      const res = await axios.post(`${API}/admin/daily-entries/shift-preview`, body());
      setPreviewResult(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const doExecute = async () => {
    if (!previewResult?.safe_to_execute) {
      toast.error("Preview not yet safe to execute");
      return;
    }
    const userName = previewResult.user?.name || previewResult.user?.email || "user";
    const ok = window.confirm(
      `Run shift for ${userName}?\n\n` +
        `• Move ${previewResult.operations.filter((o) => o.action === "move").length} docs\n` +
        `• Clear ${previewResult.clear_existing.length} docs at ${previewResult.clear_dates.join(", ") || "(none)"}\n\n` +
        `This cannot be undone.`
    );
    if (!ok) return;
    setExecuting(true);
    try {
      const res = await axios.post(`${API}/admin/daily-entries/shift-execute`, body());
      setExecuteResult(res.data);
      toast.success(`Done — moved ${res.data.total_moved}, cleared ${res.data.cleared}`);
      setPreviewResult(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Execute failed");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="admin-data-tools-tab">
      <div className="flex items-center gap-2">
        <Wrench className="w-5 h-5 text-primary" />
        <h2 className="font-mono text-lg font-bold">Data Tools</h2>
      </div>
      <p className="font-mono text-xs text-card-foreground/80 -mt-2 max-w-2xl">
        Bulk-edit utilities for support cases. Each operation runs a dry-run preview first; conflicts are surfaced
        before any data is touched.
      </p>

      <Card className="border-amber-500/40 bg-amber-500/5">
        <CardHeader className="pb-2">
          <CardTitle className="font-mono text-sm">Shift Daily Entries by ±N days</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="font-mono text-xs text-card-foreground" htmlFor="ds-user">User</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger id="ds-user" className="font-mono text-xs" data-testid="ds-user">
                  <SelectValue placeholder={loadingUsers ? "Loading…" : "Pick a user"} />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id} className="font-mono text-xs">
                      {u.name || "(no name)"} — {u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono text-xs text-card-foreground" htmlFor="ds-shift">Shift days (negative = back)</Label>
              <Input
                id="ds-shift"
                type="number"
                step="1"
                value={shiftDays}
                onChange={(e) => setShiftDays(e.target.value)}
                className="font-mono"
                data-testid="ds-shift"
              />
            </div>
            <div>
              <Label className="font-mono text-xs text-card-foreground" htmlFor="ds-start">Source start date</Label>
              <Input
                id="ds-start"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="font-mono"
                data-testid="ds-start"
              />
            </div>
            <div>
              <Label className="font-mono text-xs text-card-foreground" htmlFor="ds-end">Source end date</Label>
              <Input
                id="ds-end"
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="font-mono"
                data-testid="ds-end"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="font-mono text-xs text-card-foreground" htmlFor="ds-clear">
                Dates to clear BEFORE shift (comma-separated YYYY-MM-DD, optional)
              </Label>
              <Input
                id="ds-clear"
                value={clearDatesText}
                onChange={(e) => setClearDatesText(e.target.value)}
                className="font-mono"
                placeholder="2026-06-22"
                data-testid="ds-clear"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              onClick={doPreview}
              disabled={!userId || !start || !end || previewing || executing}
              variant="outline"
              className="btn-3d"
              data-testid="ds-preview-btn"
            >
              <Eye className="w-4 h-4 mr-1" />
              {previewing ? "Previewing…" : "Preview"}
            </Button>
            <Button
              onClick={doExecute}
              disabled={!previewResult?.safe_to_execute || executing}
              className="btn-3d"
              data-testid="ds-execute-btn"
            >
              <Play className="w-4 h-4 mr-1" />
              {executing ? "Executing…" : "Execute"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {previewResult && (
        <Card data-testid="ds-preview-card" className={previewResult.safe_to_execute ? "border-emerald-500/50" : "border-rose-500/50"}>
          <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3">
            <CardTitle className="font-mono text-sm flex items-center gap-2">
              {previewResult.safe_to_execute ? (
                <><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Preview — safe to execute</>
              ) : (
                <><AlertTriangle className="w-4 h-4 text-rose-400" /> Preview — conflicts detected</>
              )}
            </CardTitle>
            <span className="font-mono text-[10px] text-muted-foreground">
              {previewResult.user?.name || previewResult.user?.email}
            </span>
          </CardHeader>
          <CardContent className="space-y-3 text-xs font-mono">
            {previewResult.clear_existing.length > 0 && (
              <div>
                <p className="text-muted-foreground mb-1">Will clear:</p>
                <ul className="space-y-1">
                  {previewResult.clear_existing.map((d) => (
                    <li key={d} className="flex items-center gap-2"><Trash2 className="w-3 h-3 text-rose-400" />{d}</li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <p className="text-muted-foreground mb-1">Moves ({previewResult.operations.filter((o) => o.action === "move").length}):</p>
              <ul className="space-y-1 max-h-64 overflow-y-auto">
                {previewResult.operations.map((op) => (
                  <li key={op.src} className={`flex items-center gap-2 ${op.action === "move" ? "text-card-foreground" : "text-muted-foreground italic"}`}>
                    <span>{op.src}</span>
                    <ArrowRight className="w-3 h-3" />
                    <span>{op.dst}</span>
                    {op.action !== "move" && <span className="text-[10px] ml-1">({op.action})</span>}
                  </li>
                ))}
              </ul>
            </div>
            {previewResult.conflicts.length > 0 && (
              <div>
                <p className="text-rose-400 mb-1">CONFLICTS — these target dates already have data that isn&apos;t in the source range:</p>
                <ul className="space-y-1">
                  {previewResult.conflicts.map((c) => (
                    <li key={c} className="text-rose-400">• {c} (you may want to add it to the &quot;Clear&quot; list)</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {executeResult && (
        <Card className="border-emerald-500/50 bg-emerald-500/5" data-testid="ds-result-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-mono text-sm flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Done
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-xs font-mono">
            <p>Cleared: <strong>{executeResult.cleared}</strong> doc(s)</p>
            <p>Moved: <strong>{executeResult.total_moved}</strong> doc(s)</p>
            {executeResult.moved.length > 0 && (
              <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                {executeResult.moved.map((m) => (
                  <li key={m.src} className="flex items-center gap-2">
                    <span>{m.src}</span>
                    <ArrowRight className="w-3 h-3 text-emerald-400" />
                    <span>{m.dst}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DataToolsTab;
